import {Record as ImmRecord, List, Map, type RecordOf} from 'immutable';
import {
  ActionTypeConsts,
  QuickEffectIds,
} from '@packages/systems/ix2/shared-constants';

/*
 * --------------------------------------
 * Action Item Data
 * --------------------------------------
 */
import type {
  FontVariationItemConfigType,
  IX2ActionConfigType,
  IX2ActionItemType,
  IX2ActionItemGroupType,
  IX2SelectorData,
  IX2TimedActionListType,
  IX2ContinuousActionListType,
  IX2ContinuousActionGroupType,
  IX2ContinuousParameterGroupType,
  ActionConfigData,
  StartActionListConfigData,
  StopActionListConfigData,
  HideShowConfigData,
  SizeConfigData,
  Vec3PixelOrPercentConfigData,
  Vec3DegreeConfigData,
  UnitValueConfigData,
  LoopConfigData,
  ScaleConfigData,
  ContinuousActionListConfigData,
  IX2AffectedElementData,
} from '@packages/systems/ix2/types-core';
import {emptyObject} from '@packages/utilities/fp/utils';
import {values} from '@packages/utilities/key-map';
import {REGISTERED_AXIS_WGHT} from '@packages/systems/core/constants/Fonts';

import {
  FilterBlurSmall as FilterBlurIcon,
  FilterBrightnessSmall as FilterBrightnessIcon,
  FilterContrastSmall as FilterContrastIcon,
  FilterGrayscaleSmall as FilterGrayscaleIcon,
  FilterHueRotateSmall as FilterHueRotateIcon,
  FilterInvertSmall as FilterInvertIcon,
  FilterSaturateSmall as FilterSaturateIcon,
  FilterSepiaSmall as FilterSepiaIcon,
} from '@packages/systems/ui/icons/style-panel';

/*
 * --------------------------------------
 * Action Item Data
 * --------------------------------------
 */
export const actionConfig: ActionConfigData = {
  delay: 0,
  easing: '',
  duration: 500,
};

// TODO: Need to reset this if an action list is delete
export const StartActionListConfig = ImmRecord<StartActionListConfigData>(
  {
    ...actionConfig,
    actionListId: '',
    affectedElements: Map<string, IX2AffectedElementData>(),
    playInReverse: false,
    autoStopEventId: null,
  },
  'StartActionListConfig'
);

export const StopActionListConfig = ImmRecord<StopActionListConfigData>(
  {
    ...actionConfig,
    actionListId: '',
    freezePlayback: false,
  },
  'StopActionListConfig'
);

export const ContinuousActionListConfig =
  ImmRecord<ContinuousActionListConfigData>(
    {
      actionListId: '',
      affectedElements: Map(),
    },
    'ContinuousActionListConfig'
  );

// @ts-expect-error - TS2322 - Type 'Factory<{ selector: null; limitAffectedElements: null; }>' is not assignable to type 'Factory<IX2AffectedElementData>'.
export const IX2AffectedElement: ImmRecord.Factory<IX2AffectedElementData> =
  ImmRecord(
    {
      selector: null,
      limitAffectedElements: null,
    },
    'IX2AffectedElement'
  );

const {
  STYLE_SIZE,
  STYLE_FILTER,
  STYLE_FONT_VARIATION,
  STYLE_BORDER,
  GENERAL_LOOP,
  STYLE_OPACITY,
  TRANSFORM_SKEW,
  TRANSFORM_MOVE,
  TRANSFORM_SCALE,
  TRANSFORM_ROTATE,
  STYLE_TEXT_COLOR,
  GENERAL_DISPLAY,
  GENERAL_STOP_ACTION,
  GENERAL_START_ACTION,
  STYLE_BACKGROUND_COLOR,
  GENERAL_CONTINUOUS_ACTION,
  PLUGIN_LOTTIE,
  PLUGIN_SPLINE,
  PLUGIN_RIVE,
  PLUGIN_VARIABLE,
} = ActionTypeConsts;

export {PLUGIN_LOTTIE};

// Can use this directly, but you should use a convenience wrapper below
// @ts-expect-error - TS2322 - Type 'Factory<{ id: string; actionTypeId: ""; config: Map<string, null>; instant: false; }>' is not assignable to type 'Factory<IX2ActionItemType>'.
export const IX2ActionItem: ImmRecord.Factory<IX2ActionItemType> = ImmRecord(
  {
    id: '', // a static reference to the item: used when selecting items (prevent blowing away references on updates)
    actionTypeId: '', // E.g. MOVE, OPACITY, ROTATE, SKEW, etc
    // ActionConfigData + Specific data related to type
    config: Map({
      target: null,
    }),
    instant: false, // Instant action items have a 0 duration
  },
  'IX2ActionItem'
);

/*
 * --------------------------------------
 * Action Item target
 * --------------------------------------
 */

export const ACTION_ITEM_TARGET_TYPES = {
  /**
   * Action item will affect the element that it is currently assigned to
   */

  ELEMENT: 'ELEMENT',

  /**
   * Action item will affect the classes that are assigned to the target element.
   */

  ELEMENT_CLASS: 'ELEMENT_CLASS',

  /**
   * Action item will affect the element that is triggering the interaction.
   */

  TRIGGER_ELEMENT: 'TRIGGER_ELEMENT',
} as const;

export const ACTION_ITEM_TARGET_EVENT_VALUES = {
  ALL_ELEMENTS: undefined,
  CHILDREN: 'CHILDREN',
  SIBLINGS: 'SIBLINGS',
  PARENT: 'PARENT',
} as const;

/*
 * --------------------------------------
 * General Action Lists Type
 * --------------------------------------
 */

export const ACTION_TYPE_NONE = 'none';

export const ACTION_LIST_TYPES = {
  MOUSE: 'MOUSE',
  SCROLL: 'SCROLL',
  TIMED: 'TIMED',
} as const;

export const LIST_INSERTION_POINT = {
  BEGINNING: 'BEGINNING',
  END: 'END',
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
} as const;

export type IX2ActionListTypes = keyof typeof ACTION_LIST_TYPES;

/*
 * --------------------------------------
 * Timed Action Lists Data
 * --------------------------------------
 */

export const IX2TimedActionList = ImmRecord<IX2TimedActionListType>(
  {
    id: '',
    title: '',
    actionItemGroups: List(),
    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'string'.
    createdOn: null,
    useFirstGroupAsInitialState: false,
  },
  'IX2TimedActionList'
);

export const IX2TimedActionGroup: ImmRecord.Factory<IX2ActionItemGroupType> =
  ImmRecord(
    {
      actionItems: List(),
    },
    'IX2TimedActionGroup'
  );

/*
 * --------------------------------------
 * Continuous Action Lists Data
 * --------------------------------------
 */

export const IX2ContinuousActionList = ImmRecord<IX2ContinuousActionListType>(
  {
    id: '',
    title: '',
    continuousParameterGroups: List(),
    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'string'.
    createdOn: null,
  },
  'IX2ContinuousActionList'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ id: string; type: ""; parameterLabel: string; continuousActionGroups: List<IX2ContinuousActionGroupType>; }>' is not assignable to type 'Factory<IX2ContinuousParameterGroupType>'.
export const IX2ContinuousParameterGroup: ImmRecord.Factory<IX2ContinuousParameterGroupType> =
  ImmRecord(
    {
      id: '', // to "connect" to event
      type: '', // Type of continuous parameter
      parameterLabel: '', // parameter title (user editable)
      continuousActionGroups: List(), // groups of keyframed action items
    },
    'IX2ContinuousParameterGroup'
  );

export const IX2ContinuousActionGroup: ImmRecord.Factory<IX2ContinuousActionGroupType> =
  ImmRecord(
    {
      keyframe: 0,
      actionItems: List(),
    },
    'IX2ContinuousActionGroup'
  );

/*--------------------------------------------
 *
 * Action Config Types
 *
 *--------------------------------------------
 */

/**
 * IMPORTANT! Note for creating action item configurations!
 *
 * We follow a naming convention for keys used in interpolating values. For values, please add the suffix
 * "Value" to the property. For "width", use "widthValue". For units, please add the suffix
 * "Unit" to the property. for "width", use "widthUnit". For simple configs, simply "value" and "unit" are fine.
 * NOTE: The prefix _must_ match for this to work, e.g. "width" must have "widthValue" and "widthUnit"
 *
 * We use this naming convention in getOrphanActionItems(list) to detect if there is at least one
 * pair of compatible items in a continuous list.
 *
 * If two compatible action items do not exist in a continuous list,
 * the list has nothing to interpolate and appears broken. We show a warning as well.
 *
 * LS
 *
 */

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; xValue: undefined; yValue: undefined; zValue: undefined; xUnit: "PX"; yUnit: "PX"; zUnit: "PX"; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { xValue: number; yValue: number; zValue: number; xUnit: PixelOrPercent; yUnit: PixelOrPercent; zUnit: PixelOrPercent; } & IX2ActionItemBaseConfig>'.
export const Vec3PixelOrPercentConfig: ImmRecord.Factory<
  ActionConfigData & Vec3PixelOrPercentConfigData
> = ImmRecord(
  {
    ...actionConfig,
    target: undefined,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    xUnit: 'PX',
    yUnit: 'PX',
    zUnit: 'PX',
  },
  'Vec3PixelOrPercentConfig'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ value: undefined; target: undefined; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { value: DisplaySetting | null | undefined; } & IX2ActionItemBaseConfig>'.
const HideShowConfig: ImmRecord.Factory<ActionConfigData & HideShowConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      value: undefined,
      target: undefined,
    },
    'HideShowConfig'
  );

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; value: undefined; unit: "%"; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { value: number; unit: "%"; } & IX2ActionItemBaseConfig>'.
export const OpacityConfig: ImmRecord.Factory<
  ActionConfigData & UnitValueConfigData
> = ImmRecord(
  {
    ...actionConfig,
    target: undefined,
    value: undefined,
    unit: '%',
  },
  'OpacityConfig'
);

export const HEIGHT_VALUE = 'heightValue';
export const WIDTH_VALUE = 'widthValue';
export const HEIGHT_UNIT = 'heightUnit';
export const WIDTH_UNIT = 'widthUnit';

// @ts-expect-error - TS2322 - Type 'Factory<{ globalSwatchId: undefined; target: undefined; rValue: undefined; gValue: undefined; bValue: undefined; aValue: undefined; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData>'.
const ColorConfig: ImmRecord.Factory<ActionConfigData> = ImmRecord(
  {
    ...actionConfig,
    globalSwatchId: undefined,
    target: undefined,
    rValue: undefined,
    gValue: undefined,
    bValue: undefined,
    aValue: undefined,
  },
  'ColorConfig'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ locked: false; target: undefined; widthValue: undefined; heightValue: undefined; widthUnit: string; heightUnit: string; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { widthValue: number | null | undefined; heightValue: number | null | undefined; widthUnit: string; heightUnit: string; locked: boolean; } & IX2ActionItemBaseConfig>'.
const SizeConfig: ImmRecord.Factory<ActionConfigData & SizeConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      locked: false,
      target: undefined,
      [WIDTH_VALUE]: undefined,
      [HEIGHT_VALUE]: undefined,
      [WIDTH_UNIT]: 'PX',
      [HEIGHT_UNIT]: 'PX',
    },
    'SizeConfig'
  );

// @ts-expect-error - TS2322 - Type 'Factory<{ value: undefined; isInfinite: boolean; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { value: number; unit: "%"; } & IX2ActionItemBaseConfig>'.
export const LoopConfig: ImmRecord.Factory<
  ActionConfigData & UnitValueConfigData
> = ImmRecord(
  {
    ...actionConfig,
    value: undefined,
    isInfinite: false,
  },
  'LoopConfig'
);

const vec3DegreeDefault = {
  target: undefined,
  xValue: undefined,
  yValue: undefined,
  zValue: undefined,
  xUnit: 'DEG',
  yUnit: 'DEG',
  zUnit: 'DEG',
} as const;

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; xValue: undefined; yValue: undefined; zValue: undefined; xUnit: "DEG"; yUnit: "DEG"; zUnit: "DEG"; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { xValue: number; yValue: number; zValue: number; xUnit: "DEG"; yUnit: "DEG"; zUnit: "DEG"; } & IX2ActionItemBaseConfig>'.
export const Vec3DegreeConfig: ImmRecord.Factory<
  ActionConfigData & Vec3DegreeConfigData
> = ImmRecord(
  {
    ...actionConfig,
    ...vec3DegreeDefault,
  },
  'Vec3DegreeConfig'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; xValue: undefined; yValue: undefined; zValue: undefined; locked: true; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & { xValue: number; yValue: number; zValue: number; locked: boolean; } & IX2ActionItemBaseConfig>'.
export const ScaleConfig: ImmRecord.Factory<
  ActionConfigData & ScaleConfigData
> = ImmRecord(
  {
    ...actionConfig,
    target: undefined,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    locked: true,
  },
  'ScaleConfig'
);

export const FilterItemConfig = ImmRecord(
  {
    type: 'blur',
    filterId: null,
    value: 5,
    unit: 'px',
  },
  'FilterItemConfig'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; filters: List<never>; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData>'.
const FilterConfig: ImmRecord.Factory<ActionConfigData> = ImmRecord(
  {
    ...actionConfig,
    target: undefined,
    filters: List([]),
  },
  'FilterConfig'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ type: "wght"; id: null; value: number; }>' is not assignable to type 'Factory<FontVariationItemConfigType>'.
export const FontVariationItemConfig: ImmRecord.Factory<FontVariationItemConfigType> =
  ImmRecord(
    {
      type: REGISTERED_AXIS_WGHT,
      id: null,
      value: 1,
    },
    'FontVariationItemConfig'
  );

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; fontVariations: List<never>; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData>'.
const FontVariationConfig: ImmRecord.Factory<ActionConfigData> = ImmRecord(
  {
    ...actionConfig,
    target: undefined,
    fontVariations: List([]),
  },
  'FontVariationConfig'
);

type LottieConfigData = {
  target: undefined | string;
  value: number;
};

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; value: undefined; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & LottieConfigData>'.
const LottieConfig: ImmRecord.Factory<ActionConfigData & LottieConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      target: undefined,
      value: undefined,
    },
    'LottieConfig'
  );

type SplineConfigData = {
  target: undefined | string;
  value: {
    positionX?: number;
    positionY?: number;
    positionZ?: number;
    rotationX?: number;
    rotationY?: number;
    rotationZ?: number;
    scaleX?: number;
    scaleY?: number;
    scaleZ?: number;
  };
};

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; value: Map<unknown, unknown>; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & SplineConfigData>'.
const SplineConfig: ImmRecord.Factory<ActionConfigData & SplineConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      target: undefined,
      value: Map(),
    },
    'SplineConfig'
  );

type StateMachineName = string;
type StateMachineInputName = string;

type RiveConfigData = {
  target: undefined | string;
  value: {
    name: StateMachineName;
    inputs: Record<StateMachineInputName, number | boolean | undefined>;
  };
};

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; value: Map<unknown, unknown>; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & RiveConfigData>'.
const RiveConfig: ImmRecord.Factory<ActionConfigData & RiveConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      target: undefined,
      value: Map(),
    },
    'RiveConfig'
  );

type VariableConfigData = {
  target: undefined | string;
  value: {
    red?: number;
    green?: number;
    blue?: number;
    alpha?: number;
    size?: number;
    unit?: string;
  };
};

// @ts-expect-error - TS2322 - Type 'Factory<{ target: undefined; value: Map<unknown, unknown>; delay: number; easing: string; duration: number; }>' is not assignable to type 'Factory<ActionConfigData & VariableConfigData>'.
const VariableConfig: ImmRecord.Factory<ActionConfigData & VariableConfigData> =
  ImmRecord(
    {
      ...actionConfig,
      target: undefined,
      value: Map(),
    },
    'VariableConfig'
  );

type QuickEffectActionListConfigData = {
  actionListId: string | null | undefined; // the action list id;
  autoStopEventId: string;
};

// @ts-expect-error - TS2322 - Type 'Factory<{ selectorGuids: List<never>; }>' is not assignable to type 'IX2SelectorData'.
export const IX2Selector: IX2SelectorData = ImmRecord(
  {
    selectorGuids: List([]),
  },
  'IX2Selector'
);

// @ts-expect-error - TS2322 - Type 'Factory<{ actionListId: string; autoStopEventId: null; }>' is not assignable to type 'Factory<QuickEffectActionListConfigData>'.
const QuickEffectActionListConfig: ImmRecord.Factory<QuickEffectActionListConfigData> =
  ImmRecord(
    {
      actionListId: '',
      autoStopEventId: null,
    },
    'QuickEffectActionListConfig'
  );

/*
 *--------------------------------------
 * Action Lists Data
 *--------------------------------------
 */

type IX2ActionTypeData = {
  id: string;
  label: string; // Move;
  instant: boolean; // Flag for whether action item should consider easing and duration (show and hide do not);
};

type IX2ActionType = ImmRecord.Factory<IX2ActionTypeData>;

// @ts-expect-error - TS2322 - Type 'Factory<{ id: null; label: null; instant: false; }>' is not assignable to type 'IX2ActionType'.
const IXActionTypeRecord: IX2ActionType = ImmRecord(
  {
    id: null,
    label: null,
    instant: false,
  },
  'IXActionTypeRecord'
);

export const IX2MoveAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: TRANSFORM_MOVE,
  label: 'Move',
});

export const IX2ScaleAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: TRANSFORM_SCALE,
  label: 'Scale',
});

export const IX2RotateAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: TRANSFORM_ROTATE,
  label: 'Rotate',
});

export const IX2SkewAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: TRANSFORM_SKEW,
  label: 'Skew',
});

export const IX2OpacityAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord(
  {
    id: STYLE_OPACITY,
    label: 'Opacity',
  }
);

export const IX2SizeAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: STYLE_SIZE,
  label: 'Size',
});

export const IX2FilterAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: STYLE_FILTER,
  label: 'Filter',
});

export const IX2FontVariationAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: STYLE_FONT_VARIATION,
    label: 'Font variation',
  });

export const IX2LottieAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: PLUGIN_LOTTIE,
  label: 'Lottie',
});

export const IX2SplineAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: PLUGIN_SPLINE,
  label: 'Spline',
});
export const IX2RiveAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: PLUGIN_RIVE,
  label: 'Rive',
});

export const IX2VariableAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: PLUGIN_VARIABLE,
    label: 'Set variable value',
  });

export const PLUGIN_ITEMS = {
  lottie: IX2LottieAction,
  spline: IX2SplineAction,
  rive: IX2RiveAction,
} as const;

export const IX2BackgroundColorAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: STYLE_BACKGROUND_COLOR,
    label: 'BG Color',
  });

export const IX2BorderColorAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: STYLE_BORDER,
    label: 'Border Color',
  });

export const IX2TextColorAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: STYLE_TEXT_COLOR,
    label: 'Text Color',
  });

export const IX2HideShowElementAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: GENERAL_DISPLAY,
    label: 'Hide/Show',
    instant: true,
  });

export const IX2StartActionListAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: GENERAL_START_ACTION,
    label: 'Start an animation',
    instant: true,
  });

export const IX2StopActionListAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: GENERAL_STOP_ACTION,
    label: 'Stop an Animation',
    instant: true,
  });

export const IX2ContinuousActionListAction: RecordOf<IX2ActionTypeData> =
  IXActionTypeRecord({
    id: GENERAL_CONTINUOUS_ACTION,
    label: 'Control Action List',
    instant: true,
  });

export const IX2LoopAction: RecordOf<IX2ActionTypeData> = IXActionTypeRecord({
  id: GENERAL_LOOP,
  label: 'Loop',
  instant: true,
});

export const TRANSFORM_ITEMS = List([
  IX2MoveAction,
  IX2ScaleAction,
  IX2RotateAction,
  IX2SkewAction,
]);

export const STYLE_ITEMS = List([
  IX2OpacityAction,
  // IX2BoxShadowAction, // Slated for IX2.1
  IX2FilterAction, // Slated for IX2.1
  IX2FontVariationAction, // Slated for IX2.1
  IX2BackgroundColorAction,
  IX2BorderColorAction,
  IX2TextColorAction,
]);

export const GENERAL_ITEMS = List([
  // IX2ChangeComboClassAction, // Slated for IX2.1
  IX2SizeAction,
  IX2HideShowElementAction,
  // IX2StartActionListAction,
  // IX2StopActionListAction,
  // IX2LoopAction,
]);

export const GENERAL_TIMED_ITEMS = List([
  // IX2ChangeComboClassAction, // Slated for IX2.1
  IX2SizeAction,
  IX2HideShowElementAction,
  // IX2StartActionListAction,
  // IX2StopActionListAction,
  // IX2LoopAction,
]);

export const GENERAL_CONTINUOUS_ITEMS = List([
  // IX2ChangeComboClassAction, // Slated for IX2.1
  IX2SizeAction,
]);

export const VARIABLE_ITEMS = List([IX2VariableAction]);

export const ALL_ACTION_ITEMS = List([
  ...TRANSFORM_ITEMS,
  ...STYLE_ITEMS,
  ...values(PLUGIN_ITEMS),
  ...GENERAL_ITEMS,
  ...VARIABLE_ITEMS,
]);

/* ---------------------—-------------------
   Convenient Action Items with Mapped Configs
  -------------------------------------------- */

export type IX2ActionItemFactory = (
  config: IX2ActionConfigType
) => IX2ActionItemType;

const transformInstantConfig =
  (isInstant: boolean) => (config: IX2ActionConfigType) =>
    isInstant && config.has('duration') ? config.set('duration', 0) : config;

const createActionItem =
  (action: IX2ActionTypeData) =>
  (config: IX2ActionConfigType): IX2ActionItemType =>
    IX2ActionItem({
      // @ts-expect-error - Flow 0.121.0
      actionTypeId: action.id,
      config: transformInstantConfig(action.instant)(config),
      instant: action.instant,
    });

/* Plugin Items */
const IX2LottieActionItem = (config: ActionConfigData & LottieConfigData) =>
  createActionItem(IX2LottieAction)(LottieConfig(config));

const IX2SplineActionItem = (config: ActionConfigData & SplineConfigData) =>
  createActionItem(IX2SplineAction)(SplineConfig(config));

const IX2RiveActionItem = (config: ActionConfigData & RiveConfigData) =>
  createActionItem(IX2RiveAction)(RiveConfig(config));

const IX2VariableActionItem = (config: ActionConfigData & VariableConfigData) =>
  createActionItem(IX2VariableAction)(VariableConfig(config));

/* Transform Items */

export const IX2MoveActionItem = (
  config: ActionConfigData & Vec3PixelOrPercentConfigData
) => createActionItem(IX2MoveAction)(Vec3PixelOrPercentConfig(config));

export const IX2ScaleActionItem = (
  config: ActionConfigData & ScaleConfigData
) => createActionItem(IX2ScaleAction)(ScaleConfig(config));

const IX2FilterActionItem = (config: ActionConfigData) =>
  createActionItem(IX2FilterAction)(FilterConfig(config));

const IX2FontVariationsActionItem = (config: ActionConfigData) =>
  createActionItem(IX2FontVariationAction)(FontVariationConfig(config));

export const IX2RotateActionItem = (
  config: ActionConfigData & Vec3DegreeConfigData
) =>
  createActionItem(IX2RotateAction)(
    // To provide immediate feedback that action was successfully applied

    Vec3DegreeConfig(config)
  );

export const IX2SkewActionItem = (
  config: ActionConfigData & Vec3DegreeConfigData
) =>
  createActionItem(IX2SkewAction)(
    // To provide immediate feedback that action was successfully applied

    Vec3DegreeConfig(config)
  );

/* Style Items */

export const IX2OpacityActionItem = (
  config: ActionConfigData & UnitValueConfigData
) => createActionItem(IX2OpacityAction)(OpacityConfig(config));

export const IX2BackgroundColorActionItem = (
  config: ActionConfigData & UnitValueConfigData
) => createActionItem(IX2BackgroundColorAction)(ColorConfig(config));

const IX2BorderColorActionItem = (
  config: ActionConfigData & UnitValueConfigData
) => createActionItem(IX2BorderColorAction)(ColorConfig(config));

const IX2TypographyColorActionItem = (
  config: ActionConfigData & UnitValueConfigData
) => createActionItem(IX2TextColorAction)(ColorConfig(config));

export const IX2SizeActionItem = (
  config: ActionConfigData & UnitValueConfigData
) => createActionItem(IX2SizeAction)(SizeConfig(config));
/* General Items */

export const IX2HideShowElementActionItem = (config: ActionConfigData) =>
  createActionItem(IX2HideShowElementAction)(HideShowConfig(config));

export const IX2LoopActionItem = (config: ActionConfigData & LoopConfigData) =>
  createActionItem(IX2LoopAction)(LoopConfig(config));

export const IX2StartActionListActionItem = (
  config: StartActionListConfigData
) => createActionItem(IX2StartActionListAction)(StartActionListConfig(config));

export const IX2StopActionListActionItem = (config: StopActionListConfigData) =>
  createActionItem(IX2StopActionListAction)(StopActionListConfig(config));

export const IX2ControlActionListActionItem = (
  config: StopActionListConfigData
) =>
  createActionItem(IX2ContinuousActionListAction)(
    ContinuousActionListConfig(config)
  );

export const IX2QuickEffectsActionListActionItem = (
  actionTypeId: keyof typeof ActionTypeConsts | keyof typeof QuickEffectIds,
  config: Partial<QuickEffectActionListConfigData>
) =>
  IX2ActionItem({
    actionTypeId,
    config: QuickEffectActionListConfig(config),
  });

export const IX2_ACTION_ITEMS = {
  [TRANSFORM_MOVE]: IX2MoveActionItem,
  [TRANSFORM_SCALE]: IX2ScaleActionItem,
  [TRANSFORM_ROTATE]: IX2RotateActionItem,
  [TRANSFORM_SKEW]: IX2SkewActionItem,
  [STYLE_OPACITY]: IX2OpacityActionItem,
  [STYLE_BACKGROUND_COLOR]: IX2BackgroundColorActionItem,
  [STYLE_BORDER]: IX2BorderColorActionItem,
  [STYLE_TEXT_COLOR]: IX2TypographyColorActionItem,
  [STYLE_FILTER]: IX2FilterActionItem,
  [STYLE_FONT_VARIATION]: IX2FontVariationsActionItem,
  [PLUGIN_LOTTIE]: IX2LottieActionItem,
  [PLUGIN_SPLINE]: IX2SplineActionItem,
  [PLUGIN_RIVE]: IX2RiveActionItem,
  [PLUGIN_VARIABLE]: IX2VariableActionItem,
  // [STYLE_BOX_SHADOW]: null,
  [STYLE_SIZE]: IX2SizeActionItem,
  [GENERAL_DISPLAY]: IX2HideShowElementActionItem,
  [GENERAL_LOOP]: IX2LoopActionItem,
  // [GENERAL_COMBO_CLASS]: null,
  [GENERAL_STOP_ACTION]: IX2StopActionListActionItem,
  [GENERAL_START_ACTION]: IX2StartActionListActionItem,
  [GENERAL_CONTINUOUS_ACTION]: IX2ControlActionListActionItem,
} as const;

/* -----------------------------
 * Miscellaneous Types
 * ----------------------------- */

export type ActionListRenameType = {
  actionList: IX2TimedActionListType;
  title: string;
};

export const ACTION_ITEM_MOVE_POSITIONS = {
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
  INSIDE: 'INSIDE',
} as const;

type TimedActionItemsInsertParamsType = {
  afterGroup?: IX2ActionItemGroupType | null;
  afterActionItem?: IX2ActionItemType | null;
};

export const TimedActionItemsInsertParams =
  ImmRecord<TimedActionItemsInsertParamsType>(
    {
      afterGroup: null,
      afterActionItem: null,
    },
    'TimedActionItemsInsertParams'
  );

type ContinuousActionItemsInsertParamsType = {
  keyframe?: number | null;
  parameterGroup?: IX2ContinuousParameterGroupType | null;
};

export const ContinuousActionItemsInsertParams =
  ImmRecord<ContinuousActionItemsInsertParamsType>(
    {
      keyframe: null,
      parameterGroup: null,
    },
    'ContinuousActionItemsInsertParams'
  );

export type ActionItemInsertParams =
  | typeof TimedActionItemsInsertParams
  | typeof ContinuousActionItemsInsertParams;

// Can provide an optional drop action item that the moved item will be added after and whose delay it will adopt
type SelectedActionItemsMoved = {
  dropAfterActionItem?: IX2ActionItemType | null;
};

type SelectedTimedActionItemsMoved = SelectedActionItemsMoved & {
  targetActionItemGroup: IX2ActionItemGroupType | null; // item group to use for positioning;
  position: keyof typeof ACTION_ITEM_MOVE_POSITIONS | null; // where to position it regarding the target group;
};

export const TimedActionItemsMovedParams =
  ImmRecord<SelectedTimedActionItemsMoved>(
    {
      targetActionItemGroup: null,
      position: null,
      dropAfterActionItem: null,
    },
    'TimedActionItemsMovedParams'
  );

type ContinuousActionItemsMoved = SelectedActionItemsMoved & {
  targetContinuousGroup?: any | null;
  keyframe?: number;
};

export const ContinuousActionItemsMovedParams =
  ImmRecord<ContinuousActionItemsMoved>(
    {
      targetContinuousGroup: null,
      keyframe: NaN,
      dropAfterActionItem: null,
    },
    'ContinuousActionItemsMovedParams'
  );

/**
 * These are somewhat complicated types but for some reason when using Record.Factory<SelectedTimedActionItemsMoved>,
 * union type SelectedActionItemsMovedParams fails somewhere else. I'm slightly confused as to why, from a type perspective,
 * Record.Factory<SelectedTimedActionItemsMoved> !== Record<SelectedTimedActionItemsMoved> & Readonly<SelectedTimedActionItemsMoved>
 */
export type SelectedActionItemsMovedParams =
  | (ImmRecord<SelectedTimedActionItemsMoved> &
      Readonly<SelectedTimedActionItemsMoved>)
  | (ImmRecord<ContinuousActionItemsMoved> &
      Readonly<ContinuousActionItemsMoved>);

type ListOptionType = {
  label: string;
  value: string;
};

// @ts-expect-error - TS2322 - Type 'Factory<{ label: string; value: null; }>' is not assignable to type 'Factory<ListOptionType>'.
const ListOption: ImmRecord.Factory<ListOptionType> = ImmRecord(
  {
    label: '',
    value: null,
  },
  'ListOption'
);

export const TIMED_ACTION_START_TYPES = {
  AFTER_PREVIOUS_ACTION: 'AFTER_PREVIOUS_ACTION',
  WITH_PREVIOUS_ACTION: 'WITH_PREVIOUS_ACTION',
} as const;

export const ACTION_ITEM_COLOR_TYPES = {
  [IX2BackgroundColorAction.id]: IX2BackgroundColorAction.id,
  [IX2BorderColorAction.id]: IX2BorderColorAction.id,
  [IX2TextColorAction.id]: IX2TextColorAction.id,
} as const;

export const TIMED_ACTION_START_OPTIONS: List<ListOptionType> = List([
  ListOption({
    label: 'After Previous Action',
    value: TIMED_ACTION_START_TYPES.AFTER_PREVIOUS_ACTION,
  }),

  ListOption({
    label: 'With Previous Action',
    value: TIMED_ACTION_START_TYPES.WITH_PREVIOUS_ACTION,
  }),
]);

const colorEffectsRange = {
  '%': {
    min: 0,
    max: 100,
    step: 1,
  },
} as const;

const colorAdjustmentUnitRanges = {
  '%': {
    min: 0,
    max: 9999,
    step: 1,
  },
} as const;

const colorAdjustmenSliderRanges = {
  '%': {
    min: 0,
    step: 1,
    max: 200,
  },
} as const;

const hueUnitRange = {
  deg: {
    min: 0,
    max: 1440,
    step: 1,
  },
} as const;

const hueSliderRange = {
  deg: {
    min: 0,
    step: 1,
    max: 180,
  },
} as const;

const filterBlur = {
  type: 'blur',
  label: 'Blur',
  units: ['px'],
  unitRange: {px: {min: 0, max: 200, step: 1}},
  sliderRange: {px: {min: 0, max: 20, step: 1}},
  defaultValue: 5,
  defaultUnit: 'px',
  icon: FilterBlurIcon(emptyObject),
} as const;

const filterHueRotate = {
  type: 'hue-rotate',
  label: 'Hue Rotate',
  units: ['deg', 'rad', 'grad', 'turn'],
  unitRange: hueUnitRange,
  sliderRange: hueSliderRange,
  defaultValue: 180,
  defaultUnit: 'deg',
  icon: FilterHueRotateIcon(emptyObject),
} as const;

const filterSaturation = {
  type: 'saturate',
  label: 'Saturation',
  units: ['%'],
  unitRange: colorAdjustmentUnitRanges,
  sliderRange: colorAdjustmenSliderRanges,
  defaultValue: 200,
  defaultUnit: '%',
  icon: FilterSaturateIcon(emptyObject),
} as const;

const filterBrightness = {
  type: 'brightness',
  label: 'Brightness',
  units: ['%'],
  unitRange: colorAdjustmentUnitRanges,
  sliderRange: colorAdjustmenSliderRanges,
  defaultValue: 200,
  defaultUnit: '%',
  icon: FilterBrightnessIcon(emptyObject),
} as const;

const filterContrast = {
  type: 'contrast',
  label: 'Contrast',
  units: ['%'],
  unitRange: colorAdjustmentUnitRanges,
  sliderRange: colorAdjustmenSliderRanges,
  defaultValue: 200,
  defaultUnit: '%',
  icon: FilterContrastIcon(emptyObject),
} as const;

const filterGrayscale = {
  type: 'grayscale',
  label: 'Grayscale',
  units: ['%'],
  unitRange: colorEffectsRange,
  sliderRange: colorEffectsRange,
  defaultValue: 100,
  defaultUnit: '%',
  icon: FilterGrayscaleIcon(emptyObject),
} as const;

const filterSepia = {
  type: 'sepia',
  label: 'Sepia',
  units: ['%'],
  unitRange: colorEffectsRange,
  sliderRange: colorEffectsRange,
  defaultValue: 100,
  defaultUnit: '%',
  icon: FilterSepiaIcon(emptyObject),
} as const;

const filterInvert = {
  type: 'invert',
  label: 'Invert',
  units: ['%'],
  unitRange: colorEffectsRange,
  sliderRange: colorEffectsRange,
  defaultValue: 100,
  defaultUnit: '%',
  icon: FilterInvertIcon(emptyObject),
} as const;

export const FILTER_OPTIONS = [
  filterBlur,
  filterHueRotate,
  filterSaturation,
  filterBrightness,
  filterContrast,
  filterGrayscale,
  filterSepia,
  filterInvert,
] as const;
